
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, Search, Trash2, Paperclip, Calendar, X, 
  Check, ChevronLeft, ChevronRight, Upload, Edit, Eye, Camera, Image as ImageIcon, CheckCircle, Calculator, LogOut, AlertCircle, FileUp, Sun, Moon, FileText, ExternalLink
} from 'lucide-react';
import { compressImage, optimizeForAi } from '../utils/imageCompressor';
import { mockBackend, Expense, AttachmentKind } from '../services/mockBackend';
import { useNavigate } from 'react-router-dom';
import { GoogleGenAI, Type } from "@google/genai";
import { extractDataWithAI } from "../services/aiService";
import Barcode from "react-barcode";

import { AlertModal } from './AlertModal';
import { getLocalDateString } from '../src/lib/utils';
import { BrandLogo } from './BrandLogo';
import { processarBoletoExtraido, formatarLinhaDigitavel } from '../utils/boletoUtils';

const MobilePortal: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [installments, setInstallments] = useState<number>(1);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  
  const [currentExpense, setCurrentExpense] = useState<Partial<Expense>>({
    type: '',
    status: 'open',
    dueDate: getLocalDateString(),
    createdAt: getLocalDateString(),
    attachments: []
  });

  const [amountInput, setAmountInput] = useState('0,00');
  const [tempAttachment, setTempAttachment] = useState<{name: string, url: string, thumbnailUrl?: string} | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handlePreview = (att: { downloadURL: string, thumbnailUrl?: string, name: string }) => {
    const isPdf = att.name.toLowerCase().endsWith('.pdf');
    if (isPdf) {
      setPreviewUrl(att.downloadURL);
      return;
    }
    
    if (att.downloadURL && att.downloadURL.includes('lh3.googleusercontent.com')) {
      setPreviewUrl(att.downloadURL);
      return;
    }
    
    if (att.thumbnailUrl) {
      // Fix regex for new and old thumbnail patterns
      setPreviewUrl(att.thumbnailUrl.replace(/=w\d+/, '=w2000').replace(/=s\d+/, '=s2000').replace(/sz=w\d+/, 'sz=w2000'));
      return;
    }

    // Fallback for old Google Drive links
    if (att.downloadURL.includes('drive.google.com/file/d/')) {
      const match = att.downloadURL.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    } else if (att.downloadURL.includes('drive.google.com/uc?id=')) {
      const match = att.downloadURL.match(/id=([a-zA-Z0-9_-]+)/);
      if (match && match[1]) {
        setPreviewUrl(`https://drive.google.com/thumbnail?id=${match[1]}&sz=w2000`);
        return;
      }
    }
    
    setPreviewUrl(att.downloadURL);
  };

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1280);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1280);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const days = new Date(year, month + 1, 0).getDate();
    
    const result = [];
    for (let i = 0; i < firstDay; i++) {
      result.push(null);
    }
    for (let i = 1; i <= days; i++) {
      result.push(new Date(year, month, i));
    }
    return result;
  }, [currentMonth]);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getDayStatus = (date: Date) => {
    const dateStr = getLocalDateString(date);
    const dayExpenses = expenses.filter(e => e.dueDate === dateStr);
    const hasData = dayExpenses.length > 0;
    const hasPending = dayExpenses.some(e => e.status === 'open' || e.status === 'overdue');
    const allPaid = hasData && dayExpenses.every(e => e.status === 'paid');
    return { hasData, hasPending, allPaid };
  };

  const handleDayClick = (date: Date) => {
    const dateStr = getLocalDateString(date);
    if (selectedDate === dateStr) {
      openModal();
    } else {
      setSelectedDate(dateStr);
    }
  };

  useEffect(() => {
    if (user) refreshData();
  }, [user]);

  const filteredExpenses = useMemo(() => {
    let result = [...expenses];
    if (filters.status !== 'all') result = result.filter(e => e.status === filters.status);
    
    if (filters.search) {
      const term = (filters.search || "").toLowerCase();
      result = result.filter(e => 
        (e.title || "").toLowerCase().includes(term) || 
        (e.supplierName || "").toLowerCase().includes(term) ||
        String(e.amount).includes(term)
      );
    }

    // Filter by selected date
    result = result.filter(e => e.dueDate === selectedDate);

    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return result;
  }, [expenses, filters, selectedDate]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const data = await mockBackend.getExpenses(user!);
      // For 'celular' role, maybe filter to only their own expenses if needed, 
      // but the request says "visualização de lançamentos", implying they can see them.
      const cats = await mockBackend.getCategories();
      const typs = await mockBackend.getTypes();
      const vecs = await mockBackend.getVehicles();
      setExpenses(data);
      setCategories(cats);
      setTypes(typs);
      setVehicles(vecs);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const numberValue = parseInt(rawValue || '0', 10) / 100;
    setAmountInput(numberValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setCurrentExpense(prev => ({ ...prev, amount: numberValue }));
  };

  const showVehicleSelect = currentExpense.type && ((currentExpense.type || '').toLowerCase().includes('gasolina') || (currentExpense.type || '').toLowerCase().includes('combustível'));

  const openModal = (expense?: Expense, forceViewMode = false) => {
    setTempAttachment(null);
    setViewMode(forceViewMode);
    setInstallments(1);
    
    if (expense) {
      setIsEditing(true);
      setCurrentExpense(JSON.parse(JSON.stringify(expense)));
      setAmountInput((expense.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    } else {
      setIsEditing(false);
      setCurrentExpense({
        type: types[0] || 'Boleto',
        status: 'open',
        category: categories[0] || 'Geral',
        dueDate: selectedDate,
        createdAt: getLocalDateString(),
        attachments: []
      });
      setAmountInput('0,00');
    }
    setIsModalOpen(true);
  };

  const handlePdfUploadClick = () => {
    pdfInputRef.current?.click();
  };

  const handlePdfChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview local imediato
    const localUrl = URL.createObjectURL(file);
    setTempAttachment({ name: file.name, url: localUrl });

    setIsUploadingPdf(true);
    try {
      // 1. Otimização ultrarrápida da imagem (< 40ms)
      const { base64: optimizedBase64, mimeType } = await optimizeForAi(file);

      // 2. Upload não-bloqueante em background
      mockBackend.uploadImage(optimizedBase64, file.name, mimeType)
        .then((uploadResult) => {
          if (uploadResult?.url) {
            setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
          }
        })
        .catch((err) => console.warn("Upload de anexo em background:", err));

      const prompt = "Você é um assistente de OCR e contabilidade estritamente factual e de precisão matemática. Extraia APENAS os dados visíveis e legíveis neste documento financeiro brasileiro (Boleto bancário, concessionária, comprovante PIX, nota fiscal, DARF, recibo ou fatura).\n\n" +
        "Instruções cruciais para BOLETOS e CÓDIGOS DE BARRAS:\n" +
        "- linha_digitavel: Transcreva cada dígito da LINHA DIGITÁVEL do boleto com 100% de exatidão (47 dígitos numéricos para boletos bancários ou 48 dígitos para concessionárias de água/luz/telefone/tributos iniciadas em 8). A linha digitável está impressa com pontos e espaços (ex: 34191.79001 01043.510047 91020.150008 5 96450000015025). Extraia todos os dígitos.\n" +
        "- codigo_barras: Código de barras numérico de 44 dígitos, se legível.\n" +
        "- amount: Valor total a pagar numérico (float sem R$, ex: 150.25). Dica: em boletos bancários, confira se os centavos correspondem aos últimos dígitos da linha digitável.\n" +
        "- supplierName: Razão Social ou Nome do Beneficiário/Favorecido/Cedente (quem recebe o pagamento. NUNCA o pagador).\n" +
        "- title: Título curto (ex: 'Boleto [Fornecedor]' ou 'PIX - [Favorecido]').\n" +
        "- description: Descrição dos serviços ou produtos descritos.\n" +
        "- dueDate: Data de vencimento no formato YYYY-MM-DD. Se comprovante já pago, a data do pagamento.\n" +
        "- previsao_pagamento: Data prevista (YYYY-MM-DD).\n" +
        "- pix: Chave PIX ou código Copia e Cola, se houver.\n\n" +
        "Retorne estritamente um JSON com a estrutura solicitada.";

      const schema = {
        type: Type.OBJECT,
        properties: {
          docType: {
            type: Type.STRING,
            description: "Tipo do documento: boleto, comprovante_pix, nota_fiscal, outro",
          },
          supplierName: {
            type: Type.STRING,
            description: "Nome ou Razão Social do fornecedor/recebedor/beneficiário",
          },
          title: {
            type: Type.STRING,
            description: "Título curto ou identificação do lançamento",
          },
          description: {
            type: Type.STRING,
            description: "Descrição detalhada do serviço ou compra",
          },
          amount: { type: Type.NUMBER, description: "Valor total numérico a pagar" },
          dueDate: {
            type: Type.STRING,
            description: "Data de vencimento no formato YYYY-MM-DD",
          },
          previsao_pagamento: {
            type: Type.STRING,
            description: "Previsão de pagamento no formato YYYY-MM-DD",
          },
          codigo_barras: {
            type: Type.STRING,
            description: "Código de barras do boleto (somente números)",
          },
          linha_digitavel: {
            type: Type.STRING,
            description: "Linha digitável do boleto (somente números)",
          },
          pix: {
            type: Type.STRING,
            description: "Código PIX Copia e Cola ou chave PIX, se houver no boleto",
          },
        },
      };

      const extractedData = await extractDataWithAI(optimizedBase64, mimeType, prompt, schema);

      const docType = extractedData.docType;
      const expenseData: any = {};
      if (extractedData.amount !== undefined && extractedData.amount !== null) expenseData.amount = Number(extractedData.amount);
      if (extractedData.dueDate) expenseData.dueDate = extractedData.dueDate;
      if (extractedData.previsao_pagamento) expenseData.previsao_pagamento = extractedData.previsao_pagamento;
      if (extractedData.supplierName) expenseData.supplierName = extractedData.supplierName;

      // Processamento e sincronização matemática estrita de Linha Digitável e Código de Barras
      const boletoInfo = processarBoletoExtraido(extractedData.codigo_barras, extractedData.linha_digitavel);
      if (boletoInfo.codigo_barras) expenseData.codigo_barras = boletoInfo.codigo_barras;
      if (boletoInfo.linha_digitavel) expenseData.linha_digitavel = boletoInfo.linha_digitavel;

      if (extractedData.pix) expenseData.pix = extractedData.pix;
      if (extractedData.description) expenseData.description = extractedData.description;
      if (extractedData.title) expenseData.title = extractedData.title;

      const extractedType = docType === 'boleto' ? 'Boleto' : (docType === 'comprovante_pix' ? 'PIX' : 'Geral');
      const extractedCategory = 'Compras';

      setCurrentExpense((prev) => ({
        ...prev,
        ...expenseData,
        type: types.includes(extractedType) ? extractedType : (prev.type || types[0] || "Boleto"),
        category: categories.includes(extractedCategory) ? extractedCategory : (prev.category || categories[0] || "Geral"),
        createdAt: getLocalDateString(),
      }));

      if (expenseData.amount !== undefined) {
        setAmountInput(
          expenseData.amount.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
        );
      }

      if (extractedData.supplierName) {
        const isVehicle = vehicles.includes(extractedData.supplierName);
        if (isVehicle) {
          setCurrentExpense((prev) => ({ ...prev, type: "Veículo" }));
        }
      }

      if (extractedData._metadata) {
        setDocumentMetadata(extractedData._metadata);
      }

      showAlert(
        "Dados extraídos com sucesso via IA. Por favor, revise os campos antes de salvar.",
        "success"
      );

      if (!isModalOpen) {
        setIsModalOpen(true);
        setIsEditing(false);
      }
    } catch (err: any) {
      showAlert("Erro ao ler documento com IA: " + err.message);
    } finally {
      setIsUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const finalAmount = parseFloat(amountInput.replace(/\./g, '').replace(',', '.')) || 0;
      const boletoCheck = processarBoletoExtraido(currentExpense.codigo_barras, currentExpense.linha_digitavel);
      const expenseToSave = { 
        ...currentExpense, 
        amount: finalAmount,
        codigo_barras: boletoCheck.codigo_barras || currentExpense.codigo_barras || '',
        linha_digitavel: boletoCheck.linha_digitavel || currentExpense.linha_digitavel || ''
      };
      
      if (expenseToSave.status === "paid") {
        if (!expenseToSave.paidAmount) expenseToSave.paidAmount = finalAmount;
        if (!expenseToSave.paymentDate)
          expenseToSave.paymentDate =
            expenseToSave.dueDate ||
            expenseToSave.createdAt ||
            getLocalDateString();
      }

      let mainExpenseId = '';
      if (isEditing && expenseToSave.id) {
        await mockBackend.updateExpense(user!, expenseToSave.id, expenseToSave);
        mainExpenseId = expenseToSave.id;
      } else if (expenseToSave.type === 'Boleto' && installments > 1) {
        const baseDueDate = expenseToSave.dueDate ? new Date(expenseToSave.dueDate + 'T12:00:00') : new Date();
        const baseTitle = expenseToSave.title || 'Despesa';
        const originalDay = baseDueDate.getDate();
        for (let i = 0; i < installments; i++) {
            const newDate = new Date(baseDueDate);
            newDate.setMonth(baseDueDate.getMonth() + i);
            if (newDate.getDate() !== originalDay) {
                newDate.setDate(0); 
            }
            const created = await mockBackend.createExpense(user!, {
                ...expenseToSave,
                title: `${baseTitle} (${i + 1}/${installments})`,
                dueDate: getLocalDateString(newDate)
            });
            if (i === 0) mainExpenseId = created.id;
        }
      } else {
        const created = await mockBackend.createExpense(user!, expenseToSave);
        mainExpenseId = created.id;
      }

      if (tempAttachment && mainExpenseId) {
        const newAtt = await mockBackend.addAttachment(user!, mainExpenseId, tempAttachment.url, tempAttachment.name, 'comprovante', user?.id, tempAttachment.thumbnailUrl);
        setExpenses((prev) =>
          prev.map((e) =>
            e.id === mainExpenseId
              ? { ...e, attachments: [...(e.attachments || []), newAtt] }
              : e
          )
        );
      }
      setIsModalOpen(false);
      setDocumentMetadata(null);
      refreshData();
    } catch (e: any) { showAlert("Erro ao salvar: " + e.message); } 
    finally { setIsSaving(false); }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview instantâneo
    const localUrl = URL.createObjectURL(file);
    setTempAttachment({ name: file.name, url: localUrl });

    setIsUploading(true);
    try {
      const compressedBase64 = await compressImage(file);
      const uploadResult = await mockBackend.uploadImage(compressedBase64, file.name, file.type);
      if (currentExpense.id) {
          const newAtt = await mockBackend.addAttachment(user!, currentExpense.id!, uploadResult.url, file.name, 'comprovante', user?.id, uploadResult.thumbnailUrl);
          setCurrentExpense(prev => ({ ...prev, attachments: [...(prev.attachments || []), newAtt] }));
          setExpenses((prev) =>
            prev.map((e) =>
              e.id === currentExpense.id
                ? { ...e, attachments: [...(e.attachments || []), newAtt] }
                : e
            )
          );
          refreshData();
          setTempAttachment(null); // Limpa o preview após sucesso
      } else {
          setTempAttachment({ name: file.name, url: uploadResult.url, thumbnailUrl: uploadResult.thumbnailUrl });
      }
    } catch (err: any) { 
      showAlert(err.message); 
      setTempAttachment(null);
    } finally { 
      setIsUploading(false); 
      if (e.target) e.target.value = ''; 
    }
  };

  const formatCurrency = (val: number | undefined | null) => {
    if (typeof val !== 'number' || isNaN(val)) return 'R$ 0,00';
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getStatusBadge = (status: string) => {
    if (status === 'paid') return <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-green-100 text-green-700 border border-green-200">Pago</span>;
    if (status === 'overdue') return <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700 border border-red-200">Vencido</span>;
    return <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-100 text-blue-700 border border-blue-200">Aberto</span>;
  };

  const handleDeleteAttachment = async (attId: string) => {
    if (viewMode) return;
    if (confirm('Tem certeza que deseja excluir este anexo?')) {
      try {
        await mockBackend.deleteAttachment(user!, attId);
        setCurrentExpense(prev => ({
          ...prev,
          attachments: prev.attachments?.filter(a => a.id !== attId) || []
        }));
        refreshData();
      } catch (e: any) {
        showAlert("Erro ao excluir anexo: " + e.message);
      }
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setExpenseToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) return;
    try {
      await mockBackend.deleteExpense(user!, expenseToDelete);
      refreshData();
      setIsDeleteModalOpen(false);
      setExpenseToDelete(null);
    } catch (e: any) {
      showAlert("Erro ao excluir: " + e.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isDesktop) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm">
          <div className="text-purple-600 mb-4 flex justify-center"><AlertCircle size={48} /></div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Acesso Restrito</h2>
          <p className="text-slate-600 mb-6">Este portal é exclusivo para dispositivos móveis e tablets. Por favor, acesse através de um dispositivo compatível.</p>
          <button 
            onClick={handleLogout}
            className="w-full py-3 px-4 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-colors"
          >
            Sair do Sistema
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col font-sans">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col">
        {/* Header Mobile */}
        <header className="bg-gradient-to-r from-purple-800 to-indigo-900 text-white p-3.5 flex justify-between items-center shadow-md sticky top-0 z-30">
          <BrandLogo size="sm" linkTo="/dashboard" theme="glass" />
          <div className="flex items-center gap-1.5">
            <button 
              onClick={toggleDarkMode} 
              className="p-2 text-purple-100 hover:bg-white/10 rounded-full transition-colors"
              title={isDarkMode ? "Mudar para modo claro" : "Mudar para modo escuro"}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-full transition-colors">
              <LogOut size={20} />
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 space-y-4 overflow-y-auto pb-24">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">Agenda</h2>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              ref={pdfInputRef}
              onChange={handlePdfChange}
              className="hidden"
            />
            <button
              onClick={handlePdfUploadClick}
              disabled={isUploadingPdf}
              className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold uppercase text-xs flex items-center gap-2 shadow-lg active:scale-95 transition-transform disabled:opacity-50"
            >
              <FileUp size={18} /> {isUploadingPdf ? "Lendo..." : "IA"}
            </button>
            <button 
              onClick={() => openModal()} 
              className="bg-green-600 text-white px-4 py-2 rounded-full font-bold uppercase text-xs flex items-center gap-2 shadow-lg active:scale-95 transition-transform"
            >
              <Plus size={18} /> Novo
            </button>
          </div>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-4">
          <div className="space-y-4">
            {/* Calendar */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <span className="bg-purple-600 text-white px-2 py-1 rounded-lg text-lg">
                    {(currentMonth.getMonth() + 1).toString().padStart(2, '0')}
                  </span>
                  - {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
                <div className="flex gap-2">
                  <button onClick={prevMonth} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={nextMonth} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {dayNames.map(d => (
                  <div key={d} className="text-[10px] font-bold text-slate-400 uppercase">{d}</div>
                ))}
              </div>

              <div className="grid grid-cols-7 border-t border-l border-slate-200 dark:border-slate-800">
                {daysInMonth.map((date, idx) => {
                  if (!date) return <div key={`empty-${idx}`} className="aspect-square border-r border-b border-slate-200 dark:border-slate-800" />;
                  
                  const dateStr = getLocalDateString(date);
                  const isSelected = selectedDate === dateStr;
                  const isToday = getLocalDateString() === dateStr;
                  const { hasData, hasPending, allPaid } = getDayStatus(date);

                  return (
                    <button
                      key={dateStr}
                      onClick={() => handleDayClick(date)}
                      className={`
                        aspect-square flex flex-col items-center justify-center text-sm relative transition-all border-r border-b border-slate-200 dark:border-slate-800
                        ${isSelected ? 'bg-purple-600 text-white shadow-md z-10' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}
                        ${isToday && !isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : ''}
                      `}
                    >
                      <span className={isToday ? 'font-bold' : ''}>{date.getDate()}</span>
                      {hasData && (
                        <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${
                          isSelected ? 'bg-white' : 
                          hasPending ? 'bg-red-600' : 
                          allPaid ? 'bg-green-500' : 'bg-blue-400'
                        }`} />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 flex justify-between gap-4 text-[10px] text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-600"></span> Pendente</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Pago</div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Selected Date Header */}
            <div className="flex items-center gap-2 pt-2">
              <Calendar size={18} className="text-purple-600" />
              <h3 className="font-bold text-slate-800 dark:text-white">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </h3>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Pesquisar no dia..." 
                value={filters.search} 
                onChange={e => setFilters({...filters, search: e.target.value})}
                className="w-full pl-10 pr-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* List */}
            <div className="space-y-3 pb-10">
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Histórico do Dia</h4>
              {isLoading ? (
                <div className="text-center py-10 text-slate-500">Carregando...</div>
              ) : filteredExpenses.length === 0 ? (
                <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 text-center">
                  <div className="text-slate-400 text-sm mb-2">Nenhum lançamento para este dia.</div>
                  <button 
                    onClick={() => openModal()}
                    className="text-purple-600 font-bold text-xs uppercase hover:underline"
                  >
                    + Adicionar Lançamento
                  </button>
                </div>
              ) : (
                filteredExpenses.map(exp => (
                  <div 
                    key={exp.id} 
                    onClick={() => openModal(exp, true)}
                    className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm active:bg-slate-50 dark:active:bg-slate-800 transition-colors relative group"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-slate-800 dark:text-slate-200 pr-8">{exp.title}</div>
                      <div className="flex flex-col items-end gap-2">
                        {getStatusBadge(exp.status)}
                      </div>
                    </div>
                    <div className="flex justify-between items-end">
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        <div>{exp.category} • {exp.type}</div>
                        <div className="mt-1">{exp.dueDate ? new Date(exp.dueDate.includes('T') ? exp.dueDate : `${exp.dueDate}T12:00:00`).toLocaleDateString('pt-BR') : '-'}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="font-bold text-purple-700 dark:text-purple-400">{formatCurrency(exp.amount)}</div>
                        <div className="flex flex-row gap-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              openModal(exp, false);
                            }}
                            className="p-2 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg active:bg-purple-100 transition-colors"
                          >
                            <Edit size={18} />
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(exp.id);
                            }}
                            className="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg active:bg-red-100 transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 pt-6 pb-6 text-center text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-800">
          <div>2026 Gestão Fácil ERP ® • Todos os direitos reservados.</div>
          <div className="mt-1">
            Desenvolvido por{" "}
            <a 
              href="http://alonedev.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="font-bold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline transition-colors"
            >
              AloneDev.com ®
            </a>{" "}
            • Todos os direitos reservados.
          </div>
        </footer>
      </main>

      {/* Modal de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-2xl w-full max-w-sm rounded-lg flex flex-col overflow-hidden animate-in zoom-in duration-200">
            <div className="bg-red-600 text-white px-5 py-3 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-sm uppercase flex items-center gap-2">
                <Trash2 size={18} /> Confirmar Exclusão
              </h3>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="hover:text-red-200"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Confirmar exclusão definitiva?</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Esta ação não poderá ser desfeita. O lançamento será removido permanentemente.</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm text-white bg-red-600 hover:bg-red-700 font-bold uppercase rounded shadow-sm"
                >
                  Sim, Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex flex-col animate-in fade-in duration-200">
          <div className="mt-auto bg-white dark:bg-slate-900 rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-bold text-lg text-slate-800 dark:text-white">
                {isEditing ? (viewMode ? 'Visualizar' : 'Editar') : 'Novo Lançamento'}
              </h3>
              <button onClick={() => {
                setIsModalOpen(false);
                setDocumentMetadata(null);
              }} className="p-2 bg-slate-100 dark:bg-slate-800 rounded-full">
                <X size={20} />
              </button>
            </div>

            {documentMetadata && (
              <div className={`mb-6 p-4 rounded-lg border ${documentMetadata.precisa_revisao ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800/50 dark:text-amber-200' : 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800/50 dark:text-green-200'}`}>
                <div className="flex items-center gap-2 font-semibold mb-2">
                  {documentMetadata.precisa_revisao ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                  <span>
                    {documentMetadata.precisa_revisao ? 'Revisão Necessária' : 'Extração Confiável'}
                  </span>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>Tipo de Documento:</strong> {documentMetadata.tipo_documento}</p>
                  <p><strong>Confiança Média:</strong> {(documentMetadata.score_confianca * 100).toFixed(0)}%</p>
                  {documentMetadata.erros_validacao?.length > 0 && (
                    <div className="mt-2 text-red-600 dark:text-red-400">
                      <strong>Avisos de Validação:</strong>
                      <ul className="list-disc pl-5 mt-1">
                        {documentMetadata.erros_validacao.map((erro: string, idx: number) => (
                          <li key={idx}>{erro}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800/50 p-2 rounded border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                <span className="font-bold text-slate-600 dark:text-slate-400">
                  Data do Registro:
                </span>
                <input
                  type="date"
                  disabled={viewMode}
                  className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-2 py-1 font-mono text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  value={
                    currentExpense.createdAt
                      ? currentExpense.createdAt.split("T")[0]
                      : ""
                  }
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setCurrentExpense((prev) => ({
                      ...prev,
                      createdAt: newDate,
                      dueDate: newDate,
                      paymentDate:
                        prev.status === "paid"
                          ? newDate
                          : prev.paymentDate,
                    }));
                  }}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Título</label>
                <input 
                  type="text" 
                  disabled={viewMode}
                  required
                  className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex: Manutenção do Ar Condicionado"
                  value={currentExpense.title || ''} 
                  onChange={e => setCurrentExpense(prev => ({...prev, title: e.target.value}))} 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tipo</label>
                  <select 
                    disabled={viewMode}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                    value={currentExpense.type} 
                    onChange={e => setCurrentExpense(prev => ({...prev, type: e.target.value}))}
                  >
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {currentExpense.type === 'Boleto' && !currentExpense.id && (
                  <div>
                    <label className="block text-xs font-bold text-brand-500 uppercase mb-1">Qtd. Parcelas</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="60" 
                      disabled={viewMode}
                      placeholder="Ex: 3"
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border-2 border-brand-200 dark:border-brand-800 rounded-xl focus:outline-none text-brand-700 dark:text-brand-400 font-bold"
                      value={installments} 
                      onChange={e => setInstallments(Number(e.target.value))} 
                    />
                  </div>
                )}
                {showVehicleSelect && (
                  <div>
                    <label className="block text-xs font-bold text-orange-500 uppercase mb-1">Veículo</label>
                    <select 
                      disabled={viewMode}
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border-2 border-orange-200 dark:border-orange-800 rounded-xl focus:outline-none text-orange-700 dark:text-orange-400"
                      value={currentExpense.vehicle || ''} 
                      onChange={e => setCurrentExpense(prev => ({...prev, vehicle: e.target.value}))}
                    >
                      <option value="">Selecione...</option>
                      {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                )}
                {!(!showVehicleSelect) ? null : (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Categoria</label>
                    <select 
                      disabled={viewMode}
                      className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                      value={currentExpense.category} 
                      onChange={e => setCurrentExpense(prev => ({...prev, category: e.target.value}))}
                    >
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">PIX Copia e Cola / Chave PIX</label>
                <input 
                  type="text" 
                  disabled={viewMode}
                  placeholder="Cole a chave PIX ou código Copia e Cola"
                  className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                  value={currentExpense.pix || ''} 
                  onChange={e => setCurrentExpense(prev => ({...prev, pix: e.target.value}))} 
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase">Linha Digitável</label>
                  {currentExpense.linha_digitavel && (
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(currentExpense.linha_digitavel || "");
                        showAlert("Linha digitável copiada!", "success");
                      }}
                      className="text-xs text-brand-600 dark:text-brand-400 font-bold hover:underline"
                    >
                      Copiar Código
                    </button>
                  )}
                </div>
                <input 
                  type="text" 
                  disabled={viewMode}
                  placeholder="00000.00000 00000.000000 00000.000000 0 00000000000000"
                  className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none font-mono text-sm tracking-wider font-semibold text-slate-900 dark:text-slate-100"
                  value={formatarLinhaDigitavel(currentExpense.linha_digitavel || '')} 
                  onChange={e => {
                    const val = e.target.value;
                    const proc = processarBoletoExtraido(currentExpense.codigo_barras, val);
                    setCurrentExpense(prev => ({
                      ...prev, 
                      linha_digitavel: val,
                      codigo_barras: proc.codigo_barras || currentExpense.codigo_barras
                    }));
                  }} 
                />
              </div>

              {currentExpense.codigo_barras && (
                <div className="flex justify-center bg-white p-4 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto shadow-sm">
                  <Barcode 
                    value={currentExpense.codigo_barras} 
                    font="monospace" 
                    fontOptions="bold" 
                    fontSize={14} 
                    textMargin={6} 
                    width={1.6} 
                    height={55} 
                    margin={8} 
                    background="#ffffff" 
                    lineColor="#0f172a" 
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Descrição</label>
                <input 
                  type="text" 
                  disabled={viewMode}
                  placeholder="Ex: Serviço realizado pela empresa Climafrio"
                  className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                  value={currentExpense.supplierName || ''} 
                  onChange={e => setCurrentExpense(prev => ({...prev, supplierName: e.target.value}))} 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Valor (R$)</label>
                  <input 
                    type="text"
                    inputMode="numeric"
                    disabled={viewMode}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-xl focus:outline-none"
                    value={amountInput} 
                    onChange={handleAmountInputChange} 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Vencimento</label>
                  <input 
                    type="date" 
                    disabled={viewMode}
                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                    value={currentExpense.dueDate || ''} 
                    onChange={e => setCurrentExpense(prev => ({...prev, dueDate: e.target.value}))} 
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                <input
                  type="checkbox"
                  id="markAsPaidMobile"
                  checked={currentExpense.status === "paid"}
                  disabled={viewMode}
                  onChange={(e) => {
                    const isPaid = e.target.checked;
                    const currentVal = parseFloat(amountInput.replace(/\./g, "").replace(",", ".")) || 0;
                    const newPaymentDate = isPaid
                      ? currentExpense.dueDate || currentExpense.createdAt || getLocalDateString()
                      : null;

                    setCurrentExpense((prev) => ({
                      ...prev,
                      status: isPaid ? "paid" : "open",
                      paidAmount: isPaid ? currentVal : null,
                      paymentDate: newPaymentDate,
                    }));
                  }}
                  className="w-5 h-5 text-green-600 rounded focus:ring-green-500 border-gray-300 cursor-pointer"
                />
                <label htmlFor="markAsPaidMobile" className="text-sm font-bold text-slate-700 dark:text-slate-300 select-none cursor-pointer flex items-center gap-2">
                  Já está pago?
                </label>
              </div>

              {currentExpense.status === "paid" && (
                <div className="grid grid-cols-2 gap-4 bg-green-50 dark:bg-green-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800/50">
                  <div className="col-span-2 text-[10px] font-bold text-green-700 dark:text-green-400 uppercase mb-1">
                    Dados de Pagamento
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Valor Pago</label>
                    <input
                      type="text"
                      disabled
                      className="w-full p-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-green-700 dark:text-green-400"
                      value={amountInput}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data Pagto</label>
                    <input
                      type="date"
                      disabled={viewMode}
                      className="w-full p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none"
                      value={currentExpense.paymentDate || ""}
                      onChange={(e) =>
                        setCurrentExpense((prev) => ({
                          ...prev,
                          paymentDate: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              {/* Anexos Section */}
              <div className="pt-4">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Anexos / Fotos</label>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {currentExpense.attachments?.map(att => (
                    <div key={att.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                      {att.thumbnailUrl ? (
                        <img 
                          src={att.thumbnailUrl} 
                          alt="Anexo" 
                          className="w-full h-full object-cover cursor-pointer" 
                          onClick={() => handlePreview(att)} 
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = 'https://placehold.co/200x200/f8fafc/94a3b8?text=Expirado';
                          }}
                        />
                      ) : att.name.toLowerCase().endsWith('.pdf') ? (
                        <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center cursor-pointer" onClick={() => handlePreview(att)}>
                          <FileText size={24} className="text-red-500 mb-1" />
                          <span className="text-[10px] text-slate-500 truncate w-full text-center px-1">{att.name}</span>
                        </div>
                      ) : (
                        <img 
                          src={att.downloadURL} 
                          alt="Anexo" 
                          className="w-full h-full object-cover cursor-pointer" 
                          onClick={() => handlePreview(att)} 
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = 'https://placehold.co/200x200/f8fafc/94a3b8?text=Expirado';
                          }}
                        />
                      )}
                      {!viewMode && (
                        <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-bl-lg">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {tempAttachment && (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-yellow-400 animate-pulse">
                      {tempAttachment.url && !tempAttachment.name.toLowerCase().endsWith('.pdf') ? (
                        <img src={tempAttachment.url} alt="Temp" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      ) : tempAttachment.name.toLowerCase().endsWith('.pdf') ? (
                        <div className="w-full h-full bg-yellow-50 flex flex-col items-center justify-center">
                          <FileText size={24} className="text-yellow-500 mb-1" />
                          <span className="text-[10px] text-yellow-600 truncate w-full text-center px-1">{tempAttachment.name}</span>
                        </div>
                      ) : (
                        <img src={tempAttachment.thumbnailUrl || tempAttachment.url} alt="Temp" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                      )}
                      <button type="button" onClick={() => setTempAttachment(null)} className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-bl-lg">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>

                {!viewMode && (
                  <div className="grid grid-cols-2 gap-3">
                    <input type="file" ref={cameraInputRef} onChange={handleFileChange} accept="image/*" capture="environment" className="hidden" />
                    <button 
                      type="button" 
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex flex-col items-center justify-center gap-2 p-4 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-2 border-dashed border-purple-200 dark:border-purple-800 rounded-2xl active:bg-purple-100 transition-colors"
                    >
                      <Camera size={24} />
                      <span className="text-[10px] font-bold uppercase">Câmera</span>
                    </button>

                    <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*,application/pdf" className="hidden" />
                    <button 
                      type="button" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex flex-col items-center justify-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-2 border-dashed border-blue-200 dark:border-blue-800 rounded-2xl active:bg-blue-100 transition-colors"
                    >
                      <ImageIcon size={24} />
                      <span className="text-[10px] font-bold uppercase">Galeria</span>
                    </button>
                  </div>
                )}
              </div>

              {!viewMode && (
                <button 
                  type="submit" 
                  disabled={isSaving || isUploading}
                  className="w-full py-4 bg-green-600 text-white font-bold uppercase rounded-xl shadow-lg active:scale-95 transition-all disabled:opacity-50 mt-4"
                >
                  {isSaving ? 'Salvando...' : 'Salvar Lançamento'}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col p-4" onClick={() => setPreviewUrl(null)}>
          <button className="self-end text-white p-2 mb-4">
            <X size={32} />
          </button>
          <div className="flex-1 flex items-center justify-center">
            {previewUrl.toLowerCase().endsWith(".pdf") || previewUrl.includes("drive.google.com/file/d/") ? (
              <iframe
                src={previewUrl.includes('/view') ? previewUrl.replace(/\/view.*/, '/preview') : previewUrl}
                className="w-full h-full min-h-[500px]"
                title="Document Preview"
              ></iframe>
            ) : previewUrl.includes("export=download") ? (
              <div className="bg-white p-8 rounded-lg text-center max-w-sm">
                <FileText size={48} className="mx-auto text-blue-500 mb-4" />
                <h3 className="text-lg font-bold text-slate-800 mb-2">Arquivo para Download</h3>
                <p className="text-slate-500 mb-6">Este tipo de arquivo não suporta visualização direta. Clique no botão abaixo para baixar.</p>
                <a
                  href={previewUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink size={18} /> Fazer Download
                </a>
              </div>
            ) : (
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="max-w-full max-h-full object-contain" 
                referrerPolicy="no-referrer"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://placehold.co/800x600/f8fafc/94a3b8?text=Erro+ao+carregar';
                }}
              />
            )}
          </div>
          <div className="p-4 flex justify-center">
            {!previewUrl.includes("export=download") && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors shadow-lg"
              >
                <ExternalLink size={18} /> Visualizar em nova guia
              </a>
            )}
          </div>
        </div>
      )}

      {/* PWA Install Modal */}
      {/* Removido */}
      </div>
    </div>
  );
};

export default MobilePortal;
